/**
 * Tipos compartilhados do UI Shell v0.1.
 *
 * A state machine do HLOS Companion tem 3 estados canônicos (ver HLOS-1 UI Shell v0.1):
 * - onboarding: primeiro uso, sem vault local ainda (CL-07: local-first, vault ainda vazio).
 * - home: estado central, mostra histórico/contexto do aprendiz (Presence + Context Manager).
 * - conversation: turno ativo do Companion Runtime (R1-R6 serão detalhados em HLOS-4).
 *
 * HLOS-227 (HLOS-225.1): adicionado quarto estado `settings` para o usuário
 * acessar a disclosure UX (HLOS-225). Settings é acessível via link
 * no HomeState footer; transição sempre iniciada pelo usuário (Princípio V).
 *
 * As transições entre estados são SEMPRE iniciadas pelo usuário (Princípio V:
 * Sistema evolui, Aprendiz Decide). Não há background cognition (D4 do Companion Runtime).
 */

export type AppState =
  | "onboarding"
  | "home"
  | "conversation"
  | "settings"
  | "canon"
  | "sovereignty";

/**
 * Eventos que disparam transições. Modelados como união discriminada
 * para type safety exaustivo em switch statements.
 */
export type AppStateEvent =
  | { type: "INITIALIZE"; initialState: AppState }
  | { type: "ONBOARDING_COMPLETE" }
  | { type: "START_CONVERSATION" }
  | { type: "END_CONVERSATION" }
  | { type: "RETURN_HOME" }
  // HLOS-227 (HLOS-225.1): disclosure UX acessível via SettingsState.
  | { type: "OPEN_SETTINGS" }
  | { type: "CLOSE_SETTINGS" }
  // Fase 3 Lume: Canon pessoal (D3).
  | { type: "OPEN_CANON" }
  | { type: "CLOSE_CANON" }
  // Fase 4 Lume: Soberania visível (H4).
  | { type: "OPEN_SOVEREIGNTY" }
  | { type: "CLOSE_SOVEREIGNTY" };

/**
 * Função pura de transição. Sem side-effects. Sem dependências de DOM.
 * Isso permite testar a state machine isoladamente (pirâmide 70/20/10
 * da skill testing — testes unitários sem precisar montar Vue).
 */
export function transition(current: AppState, event: AppStateEvent): AppState {
  switch (event.type) {
    case "INITIALIZE":
      return event.initialState;
    case "ONBOARDING_COMPLETE":
      if (current !== "onboarding") return current;
      return "home";
    case "START_CONVERSATION":
      if (current !== "home") return current;
      return "conversation";
    case "END_CONVERSATION":
    case "RETURN_HOME":
      if (current !== "conversation") return current;
      return "home";
    case "OPEN_SETTINGS":
      if (current !== "home") return current;
      return "settings";
    case "CLOSE_SETTINGS":
      if (current !== "settings") return current;
      return "home";
    case "OPEN_CANON":
      if (current !== "home") return current;
      return "canon";
    case "CLOSE_CANON":
      if (current !== "canon") return current;
      return "home";
    case "OPEN_SOVEREIGNTY":
      if (current !== "home") return current;
      return "sovereignty";
    case "CLOSE_SOVEREIGNTY":
      if (current !== "sovereignty") return current;
      return "home";
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}
/**
 * Estado inicial. Onboarding é o estado default pois a primeira sessão
 * do usuário é, por definição, a primeira vez usando o Companion.
 */
export const INITIAL_STATE: AppState = "onboarding";
